import { InventoryState } from '@/types';

/**
 * The newest snapshot id — the single definition of "latest / live" across the
 * Inventory UI: the page default, the Statement picker, the "Latest" button and
 * label, and the auto-follow in polling/force-collect all read this. It is simply
 * the freshest state whatever its type, including the end-of-day `evening`
 * snapshot (LR-502/LR-503) — `states` is newest-first (the API orders by
 * '-created_at'). Returns undefined only for an empty list.
 */
export const getDefaultStateId = (states: InventoryState[]): number | undefined => states[0]?.id;

/**
 * Whether the current Statement selection is an explicit pin to whatever was newest
 * at the last observation — the only case the auto-follow (polling + force-collect)
 * needs to bump forward when a fresher snapshot arrives. The default view
 * (`selected === undefined`) also follows the newest, but re-resolves on its own via
 * InventoryPage's `inventoryFilters.state || defaultStateId`, so it is deliberately
 * NOT a pin here. A historical pin (`selected !== lastFollowedStateId`) stays put.
 * Single source of truth so the two auto-follow hooks cannot diverge (LR-503).
 */
export const isPinnedToFollowedState = (
  selected: number | undefined,
  lastFollowedStateId: number | null
): boolean => selected !== undefined && selected === lastFollowedStateId;

/**
 * The anchor snapshot for one calendar (UTC) day in the date picker — the day's
 * close, falling back to its open: `evening` (EOD) else `morning`. The same rule
 * applies to the in-progress day: intraday collection stops after EOD, so once the
 * `evening` snapshot exists it is the day's freshest and its anchor, exactly like a
 * completed day (LR-503). Before the close the day has no `evening` and resolves to
 * `morning`. Returns undefined if the day has neither type — the caller defensively
 * falls back to the newest state for that day. Note: this is the day's *anchor*, a
 * separate concept from the freshest snapshot (the "Latest" button → getDefaultStateId).
 */
export const getDailySnapshotState = (
  statesForDate: InventoryState[]
): InventoryState | undefined => {
  return (
    statesForDate.find((s) => s.type === 'evening') ??
    statesForDate.find((s) => s.type === 'morning')
  );
};

